import { FC, useEffect, useState } from "react";
import { Box, Tooltip } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { useListings } from "services/listings";
import websocketManager from "services/listings/webSockets";
import theme, { Theme } from "theme";

const useStyles = makeStyles<Theme>(() => ({
  websocketStatus: {
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
  connected: {
    backgroundColor: theme.custom.palette.green.main,
    boxShadow: `0 0 8px ${theme.custom.palette.green.dark}`,
    animation: "$pulse 1.5s infinite ease-in-out",
  },
  disconnected: {
    backgroundColor: theme.custom.palette.red.main,
    boxShadow: `0 0 8px ${theme.custom.palette.red.dark}`,
    animation: "none",
  },
  "@keyframes pulse": {
    "0%": {
      transform: "scale(1)",
      boxShadow: `0 0 8px ${theme.custom.palette.green.main}`,
    },
    "50%": {
      transform: "scale(1.1)",
      boxShadow: `0 0 16px ${theme.custom.palette.green.light}`,
    },
    "100%": {
      transform: "scale(1)",
      boxShadow: `0 0 8px ${theme.custom.palette.green.main}`,
    },
  },
}));

const WebsocketStatus: FC<{ listingId: number }> = ({ listingId }) => {
  const [isConnected, setIsConnected] = useState(true);
  const classes = useStyles({ isConnected });

  const {
    handleWebsocketMessage,
    isListingDataCurrent,
    listingQueries,
  } = useListings(listingId);
  const { refetch } = listingQueries;

  useEffect(() => {
    // 100ms initial delay for first render
    let delay = 100;
    let timeout: NodeJS.Timeout | null = null;

    const checkConnection = () => {
      const updatedIsConnected = websocketManager.checkStatus(listingId);
      setIsConnected(updatedIsConnected);
      delay = !!updatedIsConnected ? 1000 : 500; // check the connection more frequently if it's lost

      if (timeout) {
        clearTimeout(timeout);
      }
      timeout = setTimeout(checkConnection, delay);
    };

    checkConnection();

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [
    listingId,
    listingQueries.data?.state,
    isListingDataCurrent,
    handleWebsocketMessage,
    refetch,
  ]);

  return (
    <Tooltip
      title={
        isConnected
          ? "Connected to live updates"
          : "Lost connection to live updates, please refresh"
      }
    >
      <Box
        className={`${classes.websocketStatus} ${
          isConnected ? classes.connected : classes.disconnected
        }`}
      />
    </Tooltip>
  );
};

export default WebsocketStatus;
